import { Accordion, Modal, Table } from 'semantic-ui-react';
import Icon from 'semantic-ui-react/dist/commonjs/elements/Icon';
import * as React from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { BillDocumentDto, MandateTariffDisplayDto, TariffDto, UpsertMandateTariffDto } from 'ts-types/api.types';
import styled from 'styled-components';
import UpsertMandateTariffForm from 'components/mandate-settings/UpsertMandateTariffForm';
import { getMandateTariffById } from 'services/mandateTariffServices';
import axios, { CancelTokenSource } from 'axios';
import { getAllTariffs } from 'services/tariffServices';
import { getAllBillDocuments } from 'services/billDocumentServices';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import EditActionButton from 'components/EditActionButton';
import DeleteActionButton from 'components/DeleteActionButton';
import { noop } from 'utils/functionUtils';

const ScrollableTableContainer = styled.div`
  height: 252px;
  overflow-y: auto;
  width: 100%;
  border: 1px solid rgba(34,36,38,.15);
  box-shadow: none;
  border-radius: .28571429rem;

  & .ui.table {
    border: none;
  }

   & .ui.table thead tr:first-child > th {
      position: sticky !important;
      top: 0;
      z-index: 2;
      background: #fff;
  }

  & .ui.table tbody tr:last-child > td {
      border-bottom: 1px solid rgba(34,36,38,.15)
  }
`;

interface Props extends MessageSourceProps {
  mandateTariffs: Array<MandateTariffDisplayDto>,
  addEditedMandateTariff: (mandateTariff: Partial<UpsertMandateTariffDto>) => void,
  isMandateTariffDeleted: (tariffId: number) => boolean,
  getEditedMandateTariff: (tariffId: number) => Partial<UpsertMandateTariffDto>
}

interface State {
  activeIndex: number,
  editBillConfiguration: boolean,
  editMandateTariff?: Partial<UpsertMandateTariffDto>,
  tariffs: TariffDto[],
  displayMandateTariffs: Array<Partial<MandateTariffDisplayDto>>,
  billDocuments: Array<BillDocumentDto>,
  cancelTokenSource: CancelTokenSource
}

class BillConfigurationsSection extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      activeIndex: -1,
      editBillConfiguration: false,
      tariffs: [],
      displayMandateTariffs: props.mandateTariffs,
      billDocuments: [],
      cancelTokenSource
    };

    getAllTariffs(cancelTokenSource).then((tariffs) => {
      this.setState({
        tariffs: tariffs
      })
    })
  }

  render(): React.ReactNode {
    const {getMessage} = this.props;
    const {activeIndex, editBillConfiguration} = this.state;

    return (
        <Accordion fluid>
          <Accordion.Title active={activeIndex === 0}
                           index={0}
                           onClick={this.toggleAccordion}>
            <h3><Icon name='dropdown'/>{getMessage("mandate.billConfigurations")}</h3>
          </Accordion.Title>
          <Accordion.Content active={activeIndex === 0}>
            {this.renderBillConfigurationsTable()}
            { editBillConfiguration &&
              this.renderUpsertMandateTariffModal()
            }
          </Accordion.Content>
        </Accordion>
    )
  }

  renderBillConfigurationsTable = () => {
    const {getMessage} = this.props;

    return (
        <ScrollableTableContainer>
          <Table celled selectable size='small'>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell width={2}>{getMessage('mandate.billConfigurations.tariff')}</Table.HeaderCell>
                <Table.HeaderCell width={2}>{getMessage('mandate.billConfigurations.xmlType')}</Table.HeaderCell>
                <Table.HeaderCell width={2}>
                  {getMessage('mandate.billConfigurations.billPrintoutType')}
                </Table.HeaderCell>
                <Table.HeaderCell width={2}>
                  {getMessage('mandate.billConfigurations.reminderPrintoutType')}
                </Table.HeaderCell>
                <Table.HeaderCell width={2}>{getMessage('actions')}</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {this.renderTableRows()}
            </Table.Body>
          </Table>
        </ScrollableTableContainer>
    )
  };

  renderTableRows = () => {
    const {tariffs, displayMandateTariffs} = this.state;
    const {getMessage} = this.props;

    let rows: React.ReactNodeArray = [];
    tariffs.forEach(tariff => {
      let mandateTariff = displayMandateTariffs.find(mt => tariff.id === mt.tariffId);
      let tableRow = (
          <Table.Row key={tariff.id}>
            <Table.Cell width={2}>
              {tariff ? tariff.description : ""}
            </Table.Cell>
            <Table.Cell width={2}>
              {mandateTariff ? mandateTariff.xmltype : ""}
            </Table.Cell>
            <Table.Cell width={2}>
              {mandateTariff ? mandateTariff.billPrintoutType : ""}
            </Table.Cell>
            <Table.Cell width={2}>
              {mandateTariff ? mandateTariff.reminderPrintoutType : ""}
            </Table.Cell>
            <Table.Cell width={2}>
              <EditActionButton
                  hoverMessage={getMessage('msgEdit')}
                  onConfirm={() => this.editMandateTariff(tariff.id, mandateTariff)}
              />
              <DeleteActionButton
                  hoverMessage={getMessage('msgDelete')}
                  onConfirm={() => this.deleteMandateTariff(tariff)}
              />
            </Table.Cell>
          </Table.Row>
      );
      rows.push(tableRow);
    });
    return rows;
  };

  editMandateTariff = (tariffId: number, mandateTariff?: Partial<MandateTariffDisplayDto>): void => {
    const {isMandateTariffDeleted, getEditedMandateTariff} = this.props;
    const {cancelTokenSource} = this.state;

    getAllBillDocuments(cancelTokenSource).then(billDocuments => {
      if (mandateTariff && !isMandateTariffDeleted(mandateTariff.tariffId!)) {
        const foundEditedMandateTariff = getEditedMandateTariff(mandateTariff.tariffId!);
        if (!foundEditedMandateTariff && mandateTariff.id) {
          getMandateTariffById(mandateTariff.id, cancelTokenSource).then((mt: UpsertMandateTariffDto) => {
            this.setState({
              editMandateTariff: mt,
              billDocuments: billDocuments
            });
          }).catch(noop).finally(() => {
            this.setState({
              editBillConfiguration: true
            })
          });
        } else {
          this.setState({
            editBillConfiguration: true,
            editMandateTariff: foundEditedMandateTariff,
            billDocuments: billDocuments
          })
        }
      } else {
        this.setState({
          editBillConfiguration: true,
          billDocuments: billDocuments,
          editMandateTariff: {
            tariffId: tariffId
          }
        });
      }
    });

  };

  deleteMandateTariff = (tariff: TariffDto) => {
    const {addEditedMandateTariff} = this.props;
    const {displayMandateTariffs} = this.state;

    let newMandateTariff: Partial<UpsertMandateTariffDto> = {
      tariffId: tariff.id,
      toBeDeleted: true
    };

    this.setState({
      displayMandateTariffs: displayMandateTariffs.filter(mt => mt.tariffId !== tariff.id)
    });

    addEditedMandateTariff(newMandateTariff);
  };

  toggleAccordion = (e: any, titleProps: any) => {
    const {index} = titleProps;
    const {activeIndex} = this.state;

    const newIndex = activeIndex === index ? -1 : index;
    this.setState({activeIndex: newIndex})
  };

  renderUpsertMandateTariffModal = (): React.ReactNode => {
    const {getMessage} = this.props;
    const {editBillConfiguration, editMandateTariff, billDocuments} = this.state;

    return (
        <Modal
            open={editBillConfiguration}
            onClose={this.hideModal}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("bill.billConfiguration")}</Modal.Header>
          <FormModalContentContainer>
            <UpsertMandateTariffForm editMandateTariff={editMandateTariff} onSave={this.onSaveUpsertForm}
                                     onCancel={this.hideModal} billDocuments={billDocuments}/>
          </FormModalContentContainer>
        </Modal>
    )
  };

  onSaveUpsertForm = (mandateTariff: Partial<UpsertMandateTariffDto>) => {
    const {addEditedMandateTariff} = this.props;
    const {displayMandateTariffs} = this.state;
    let newDisplayMandateTariffs: Array<Partial<MandateTariffDisplayDto>> = displayMandateTariffs
        .filter(mt => mt.tariffId !== mandateTariff.tariffId);

    newDisplayMandateTariffs.push({
      id: mandateTariff.id,
      tariffId: mandateTariff.tariffId,
      xmltype: mandateTariff.xmltype,
      billPrintoutType: mandateTariff.billPrintoutType,
      reminderPrintoutType: mandateTariff.reminderPrintoutType
    });

    this.setState({
      displayMandateTariffs: newDisplayMandateTariffs
    });

    addEditedMandateTariff(mandateTariff);
    this.hideModal();
  };

  hideModal = () => {
    this.setState({
      editBillConfiguration: false,
      editMandateTariff: undefined
    });
  };

}

export default withMessages(BillConfigurationsSection);
